/**
 * Bereich (Area) — die vier Produkt-Bereiche von NutriFlow Pro:
 *
 *   • patient   — Patienten-Portal (PWA)
 *   • klinik    — Fachkräfte in der Klinik (stationär)
 *   • ambulant  — Fachkräfte in der ambulanten Praxis
 *   • admin     — Plattform-Administration (Icarus)
 *
 * Der Bereich ist die *visuelle Identitäts*-Achse: jede Area setzt über die
 * `.area-*`-Klasse am <html> nur ihre Akzentfarbe (`--primary`/`--accent`/
 * `--ring`) — alles andere teilt sich die gemeinsame Basis (`src/index.css`).
 *
 * `deriveArea` ist eine reine Funktion (kein React), damit die Precedence
 * unabhängig getestet werden kann — analog zu `post-login-redirect.ts`.
 *
 * Bewusst getrennt von `setting_type` (praxis|klinik): das bleibt die
 * Geschäfts-/Lizenzlogik (Routing, Scope-Filter). Hier wird `setting_type`
 * nur auf die passende Fachkräfte-Area gemappt (praxis → ambulant).
 */

import { workspaceFromPath } from './post-login-redirect';
import type { SettingType } from '@/hooks/useFeatures.constants';

export type Area = 'patient' | 'klinik' | 'ambulant' | 'admin';

export const AREAS: readonly Area[] = ['patient', 'klinik', 'ambulant', 'admin'] as const;

/** Fallback-Bereich, wenn nichts anderes greift (ambulante Praxis = Default). */
export const DEFAULT_AREA: Area = 'ambulant';

/** Mappt den Org-`setting_type` auf die entsprechende Fachkräfte-Area. */
function areaFromSettingType(s: SettingType | null | undefined): Area | null {
  if (s === 'klinik') return 'klinik';
  if (s === 'praxis') return 'ambulant';
  return null;
}

export interface AreaInput {
  /** Aktueller Pfad (z. B. aus `useLocation().pathname`). */
  pathname: string;
  /** Aktueller Query-String inkl. `?` (z. B. aus `useLocation().search`). */
  search: string;
  isDemo: boolean;
  demoRole: 'therapeut' | 'patient' | null;
  demoSettingType: SettingType | null;
  /** `setting_type` der authentifizierten Org (aus `useFeatures`). */
  settingType: SettingType | null;
}

/**
 * Bestimmt den aktiven Bereich. Precedence (höchste zuerst):
 *
 *   1. Expliziter Login-Workspace-Pfad (`/login/<ws>`).
 *   2. Explizite App-Pfad-Präfixe (`/admin*` → admin, `/portal*` → patient).
 *   3. `?mode=praxis|klinik` (Landing → Login ohne Flicker).
 *   4. Demo-Session (`demoRole` / `demoSettingType`).
 *   5. `setting_type` der authentifizierten Org.
 *   6. Landing-Pfad-Heuristik (`/klinik*` / `/praxis*`).
 *   7. Default (`ambulant`).
 */
export function deriveArea(input: AreaInput): Area {
  const { pathname, search, isDemo, demoRole, demoSettingType, settingType } = input;

  // 1. Expliziter Login-Workspace — `workspaceFromPath` liefert direkt die Area.
  const ws = workspaceFromPath(pathname);
  if (ws) return ws;

  // 2. Explizite App-Pfad-Präfixe.
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return 'admin';
  if (pathname === '/portal' || pathname.startsWith('/portal/')) return 'patient';

  // 3. Query-Param (Landing → Login). Nutzt dieselbe SettingType-Sprache.
  const queryArea = areaFromSettingType(
    new URLSearchParams(search).get('mode') as SettingType | null,
  );
  if (queryArea) return queryArea;

  // 4. Demo-Session.
  if (isDemo) {
    if (demoRole === 'patient') return 'patient';
    const demoArea = areaFromSettingType(demoSettingType);
    if (demoArea) return demoArea;
  }

  // 5. Authentifizierte Org.
  const orgArea = areaFromSettingType(settingType);
  if (orgArea) return orgArea;

  // 6. Landing-Pfad-Heuristik.
  if (pathname.startsWith('/klinik')) return 'klinik';
  if (pathname.startsWith('/praxis')) return 'ambulant';

  // 7. Default.
  return DEFAULT_AREA;
}
